import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	Box,
	Flex,
	Text,
	Button,
	useToast,
	SimpleGrid,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalFooter,
	ModalBody,
	ModalCloseButton,
	useDisclosure,
} from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import TextFormField from '../../../../components/form/TextFormField';
import DateInputFormField from '../../../../components/form/DateInputFormField';

import { gql, useMutation } from '@apollo/client';
import moment from 'moment';
import useStore from '../../../../store';
import { faPenSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import c from '../../../../constant';
import getPositionLabel from '../../../../utils/getPositionLabel';
import TextFormFieldReadOnly from '../../../../components/form/TextFormFieldReadOnly';
import PreUploadFileFormField from '../../../../components/form/PreUploadFileFormField';
import DownloadAndViewButtonUserUpload from '../../../../components/Files/DownloadAndViewButtonUserUpload';
import DropDownField from '../../../../components/form/DropDownField';
import DownloadAndViewButton from '../../../../components/Files/DownloadAndViewButton';

const UPDATE_EMPLOYEE_MUTATION = gql`
	mutation update($id: String!, $currentWorkingStatus: String, $firstWorkingPlace: String) {
		updateEmployeeFirstWorkingPlaceAndCurrentWorkingStatus(id: $id, currentWorkingStatus: $currentWorkingStatus, firstWorkingPlace: $firstWorkingPlace)
	}
`;

const signUpSchema = Yup.object().shape({
	firstWorkingstartDate: Yup.string().nullable(),
	firstWorkingPassedDate: Yup.string().nullable(),
	firstWorkingPosition: Yup.string(),
	firstWorkingInstitution: Yup.string(),
	firstWorkingJobTitle: Yup.string(),
	firstWorkingOrganization: Yup.string(),
	firstWorkingDepartment: Yup.string(),
	firstWorkingOffice: Yup.string(),
	documentFirst: Yup.string(),

	currentWorkingStatusPosition: Yup.string().nullable(),
	currentWorkingStatusDateOfChangePosition: Yup.string().nullable(),
	currentWorkingStatusJobTitle: Yup.string().nullable(),
	currentWorkingStatusDateOfChangeJobTitle: Yup.string().nullable(),
	currentWorkingStatusOrganization: Yup.string().default('និយ័តករធានារ៉ាប់រងកម្ពុជា'),
	documentCurrent: Yup.string().nullable(),
});

function FirstWorkingPlaceAndCurrentWorkingStatus({ id, initData, callbackOnSuccess }) {
	const toast = useToast();
	const { t } = useTranslation();
	const [updateEmployee, { error, data, loading }] = useMutation(UPDATE_EMPLOYEE_MUTATION);
	const { currentLang } = useStore((state) => state.currentLang);
	const { currentUser } = useStore((state) => state.currentUser);
	moment.locale(currentLang === 'kh' ? 'km' : 'en-gb');

	const [isEditing, setIsEditing] = useState(false);

	const { isOpen, onClose, onOpen } = useDisclosure();

	useEffect(() => {
		if (error) {
			if (error?.graphQLErrors?.length > 0) {
				error?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					}),
				);
			} else {
				toast({
					title: t(error?.message ? error?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [error]);

	useEffect(() => {
		if (data) {
			if (data.updateEmployeeFirstWorkingPlaceAndCurrentWorkingStatus) {
				toast({
					title: t('Officials updated'),
					description: t('Officials updated successfully'),
					status: 'success',
					duration: 5000,
					isClosable: true,
					position: 'bottom-right',
				});
				callbackOnSuccess();
			}
		}
	}, [data]);

	function viewFile(value) {
		const tmpValue = value ? JSON.parse(value) : null;
		return tmpValue ? <DownloadAndViewButtonUserUpload url={tmpValue?.file} size={tmpValue?.fileSize} /> : '';
	}

	function rowData({ labelEN, labelKH, value }) {
		return (
			<Flex alignItems="center">
				<Text fontWeight="bold">{currentLang === 'en' ? labelEN : labelKH}</Text>:<Text ml="4">{value || ''}</Text>
			</Flex>
		);
	}

	function getStatusJobTitle(statusJobTitle) {
		if (statusJobTitle) {
			switch (statusJobTitle) {
				case 'Level_7':
					return 'អនុមន្រ្តីកម្រិត១';
				case 'Level_6':
					return 'អនុមន្រ្តីកម្រិត២';
				case 'Level_5':
					return 'អនុមន្រ្តីកម្រិត៣';
				case 'Level_4':
					return 'នាយក្រមការ';
				case 'Level_3':
					return 'ក្រមការដើមខ្សែ';
				case 'Level_2':
					return 'ក្រមការ';
				case 'Level_1':
					return 'កម្មសិក្សា';
				default:
					return statusJobTitle;
			}
		} else {
			return '';
		}
	}

	function getCurrentWorkingStatusDepartmentLabel(currentDepartment) {
		if (currentDepartment) {
			switch (currentDepartment) {
				case 'D_G_A':
					return 'នាយកដ្ឋានរដ្ឋបាលទូទៅ';
				case 'D_L_L_A':
					return 'នាយកដ្ឋានគ្រប់គ្រងអាជ្ញាបណ្ណ និងកិច្ចការគតិយុត្ត';
				case 'D_S':
					return 'នាយកដ្ឋានត្រួតពិនិត្យ';
				case 'D_M_D':
					return 'នាយកដ្ឋានអភិវឌ្ឍន៍ទីផ្សារ';
				case 'D_R_T_C':
					return 'នាយកដ្ឋានស្រាវជ្រាវ បណ្តុះបណ្តាល និងសហប្រតិបត្តិការ';
				default:
					return '';
			}
		}
	}

	function getCurrentWorkingStatusOfficeLabel(currentOffice) {
		if (currentOffice) {
			switch (currentOffice) {
				case 'office1':
					return 'ការិយាល័យរដ្ឋបាល';
				case 'office2':
					return 'ការិយាល័យគណនេយ្យ';
				case 'office3':
					return 'ការិយាល័យធនធានមនុស្ស';
				case 'office4':
					return 'ការិយាល័យទំនាក់ទនងសាធារណៈ';
				case 'office5':
					return 'ការិយាល័យគ្រប់គ្រងអាជ្ញាបណ្ណ';
				case 'office6':
					return 'ការិយាល័យចុះបញ្ជីអន្តរការី';
				case 'office7':
					return 'ការិយាល័យនីតិកម្ម និងផ្សះផ្សាវិវាទ';
				case 'office8':
					return 'ការិយាល័យត្រួតពិនិត្យក្រុមហ៊ុនធានារ៉ាប់រងអាយុជីវិត';
				case 'office9':
					return 'ការិយាល័យត្រួតពិនិត្យក្រុមហ៊ុនធានារ៉ាប់រងទូទៅ';
				case 'office10':
					return 'ការិយាល័យត្រួតពិនិត្យក្រុមហ៊ុនធានារ៉ាប់រងខ្នាតតូច​ និង​អន្តរការីធានារ៉ាប់រង';
				case 'office11':
					return 'ការិយាល័យបទប្បញ្ញត្តិប្រុងប្រយ័ត្ន';
				case 'office12':
					return 'ការិយាល័យផលិតផលធានារ៉ាប់រងអាយុជីវិត';
				case 'office13':
					return 'ការិយាល័យផលិតផលធានារ៉ាប់រងទូទៅ';
				case 'office14':
					return 'ការិយាល័យផលិតផលធានារ៉ាប់រងខ្នាតតូច';
				case 'office15':
					return 'ការិយាល័យត្រួតពិនិត្យប្រតិបត្តិការទីផ្សារ';
				case 'office16':
					return 'ការិយាល័យស្រាវជ្រាវ';
				case 'office17':
					return 'ការិយាល័យបណ្តុះបណ្តាល';
				case 'office18':
					return 'ការិយាល័យសហប្រតិបត្តិការ';
				case 'office19':
					return 'ការិយាល័យបច្ចេកវិទ្យាធានារ៉ាប់រង';
				default:
					return '';
			}
		}
	}

	function displayData() {
		return (
			<Box p="26px">
				<Text fontSize="lg" fontWeight="bold" mb="4" color="#2980b9">
					ក. ចូលបម្រើការងាររដ្ឋដំបូង
				</Text>
				<SimpleGrid columns={[1, 2, 3]} spacing={2}>
					{rowData({
						labelEN: 'កាលបរិច្ឆេទចូលបំរើការងាររដ្ឋដំបូង',
						labelKH: 'កាលបរិច្ឆេទចូលបំរើការងាររដ្ឋដំបូង',
						value: initData?.firstWorkingstartDate ? moment(initData?.firstWorkingstartDate).format(c.excelDateOnlyTextFormat) : '',
					})}
					{rowData({
						labelEN: 'កាលបរិច្ឆេទតាំងស៊ប់',
						labelKH: 'កាលបរិច្ឆេទតាំងស៊ប់',
						value: initData?.firstWorkingPassedDate ? moment(initData?.firstWorkingPassedDate).format(c.excelDateOnlyTextFormat) : '',
					})}
					{rowData({ labelEN: 'មុខតំណែង', labelKH: 'មុខតំណែង', value: initData?.firstWorkingPosition })}
					{rowData({ labelEN: 'ក្រសួង/ស្ថាប័ន', labelKH: 'ក្រសួង/ស្ថាប័ន', value: initData?.firstWorkingInstitution })}
					{rowData({
						labelEN: 'ក្របខ័ណ្ឌ ឋានន្តរស័ក្តិ និងថ្នាក់',
						labelKH: 'ក្របខ័ណ្ឌ ឋានន្តរស័ក្តិ និងថ្នាក់',
						value: getStatusJobTitle(initData?.firstWorkingJobTitle),
					})}
					{rowData({ labelEN: 'អង្គភាព', labelKH: 'អង្គភាព', value: initData?.firstWorkingOrganization })}
					{rowData({ labelEN: 'នាយកដ្ឋាន/អង្គភាព/មន្ទីរ', labelKH: 'នាយកដ្ឋាន/អង្គភាព/មន្ទីរ', value: initData?.firstWorkingDepartment })}
					{rowData({ labelEN: 'ការិយាល័យ', labelKH: 'ការិយាល័យ', value: initData?.firstWorkingOffice })}
					{rowData({ labelEN: 'ឯកសារយោង', labelKH: 'ឯកសារយោង', value: viewFile(initData?.documentFirst) })}
				</SimpleGrid>
				<Text fontSize="lg" fontWeight="bold" mt="4" mb="4" color="#2980b9">
					ខ. ស្ថានភាពមុខងារបច្ចុប្បន្ន
				</Text>

				<SimpleGrid columns={[1, 2, 3]} spacing={2}>
					{rowData({
						labelEN: 'មុខតំណែង',
						labelKH: 'មុខតំណែង',
						value: initData?.position ? getPositionLabel(initData?.position) : 'N/A',
					})}
					{rowData({
						labelEN: 'កាលបរិច្ឆេទប្តូរក្របខ័ណ្ឌ ឋានន្តរស័ក និងថ្នាក់ចុងក្រោយ',
						labelKH: 'កាលបរិច្ឆេទប្តូរក្របខ័ណ្ឌ ឋានន្តរស័ក និងថ្នាក់ចុងក្រោយ',
						value: initData?.dateOfLastRank ? moment(initData?.dateOfLastRank).format(c.excelDateOnlyTextFormat) : 'N/A',
					})}
					{rowData({ labelEN: 'អង្គភាព', labelKH: 'អង្គភាព', value: 'និយ័តករធានារ៉ាប់រងកម្ពុជា' })}
					{rowData({
						labelEN: 'ក្របខ័ណ្ឌ ឋានន្តរស័ក និងថ្នាក់',
						labelKH: 'ក្របខ័ណ្ឌ ឋានន្តរស័ក និងថ្នាក់',
						value: getStatusJobTitle(initData?.rank),
					})}
					{rowData({
						labelEN: 'កាលបរិច្ឆេទទទួលមុខតំណែងចុងក្រោយ',
						labelKH: 'កាលបរិច្ឆេទទទួលមុខតំណែងចុងក្រោយ',
						value: initData?.dateOfLastPosition ? moment(initData?.dateOfLastPosition).format(c.excelDateOnlyTextFormat) : 'N/A',
					})}
					{rowData({
						labelEN: 'ឯកសារយោង',
						labelKH: 'ឯកសារយោង',
						value: <DownloadAndViewButton url={initData?.attachmentFile?.file} size={initData?.attachmentFile?.size} />,
					})}
					{rowData({
						labelEN: 'នាយកដ្ឋាន',
						labelKH: 'នាយកដ្ឋាន',
						value: getCurrentWorkingStatusDepartmentLabel(initData?.department),
					})}
					{rowData({
						labelEN: 'ការិយាល័យ',
						labelKH: 'ការិយាល័យ',
						value: getCurrentWorkingStatusOfficeLabel(initData?.office),
					})}
				</SimpleGrid>
			</Box>
		);
	}

	function checkEmployeeEditPermission(roles) {
		const rolesBreakdown = roles?.split(',') ?? [];
		if (rolesBreakdown.includes('admin') || rolesBreakdown?.includes('employee')) {
			return false;
		} else return true;
	}

	return (
		<>
			<Box mt="3">
				<Flex mt="4" mb="4" alignItems="center">
					<Text fontSize="xl" fontWeight="bold" color="#c0392b">
						២. ព័ត៌មានអំពីស្ថានភាពមុខងារសាធារណៈ/មន្ត្រីលក្ខន្តិកៈ
					</Text>

					{isEditing && (
						<Button variant="outline" size="sm" ml="4" borderRadius={c.borderRadius} onClick={() => setIsEditing(false)} colorScheme="gray">
							{t(`Cancel`)}
						</Button>
					)}
					{!isEditing && (
						<Button
							variant="outline"
							size="sm"
							ml="4"
							borderRadius={'0px !important'}
							leftIcon={<FontAwesomeIcon icon={faPenSquare} style={{ fontSize: 14 }} />}
							onClick={() => onOpen()}
							colorScheme="whatsapp"
						>
							{t(`Edit`)}
						</Button>
					)}
				</Flex>

				{!isEditing && <>{displayData()}</>}
				<Modal scrollBehavior="inside" size="5xl" isOpen={isOpen} onClose={onClose}>
					<ModalOverlay backdropFilter="auto" bg="blackAlpha.300" backdropBlur="2px" />
					<Formik
						initialValues={{
							firstWorkingstartDate: initData?.firstWorkingstartDate || '',
							firstWorkingPassedDate: initData?.firstWorkingPassedDate || '',
							firstWorkingPosition: initData?.firstWorkingPosition || '',
							firstWorkingInstitution: initData?.firstWorkingInstitution || '',
							firstWorkingJobTitle: initData?.firstWorkingJobTitle || '',
							firstWorkingOrganization: initData?.firstWorkingOrganization || '',
							firstWorkingDepartment: initData?.firstWorkingDepartment || '',
							firstWorkingOffice: initData?.firstWorkingOffice || '',
							documentFirst: initData?.documentFirst || '',

							currentWorkingStatusPosition: initData?.position || '',
							currentWorkingStatusDateOfChangePosition: initData?.dateOfLastPosition || '',
							currentWorkingStatusJobTitle: initData?.rank || '',
							currentWorkingStatusDateOfChangeJobTitle: initData?.dateOfLastRank || '',
							currentWorkingStatusOrganization: 'និយ័តករធានារ៉ាប់រងកម្ពុជា',
							documentCurrent: null,
							currentWorkingStatusDepartment: initData?.department || '',
							currentWorkingStatusOffice: initData?.office || '',
						}}
						validationSchema={signUpSchema}
						onSubmit={(values) => {
							updateEmployee({
								variables: {
									id,
									currentWorkingStatus: JSON.stringify({
										currentWorkingStatusPosition: values?.currentWorkingStatusPosition,
										currentWorkingStatusDateOfChangePosition: values?.currentWorkingStatusDateOfChangePosition,
										currentWorkingStatusJobTitle: values?.currentWorkingStatusJobTitle,
										currentWorkingStatusDateOfChangeJobTitle: values?.currentWorkingStatusDateOfChangeJobTitle,
										currentWorkingStatusOrganization: values?.currentWorkingStatusOrganization
											? values?.currentWorkingStatusOrganization
											: 'និយ័តករធានារ៉ាប់រងកម្ពុជា',
										documentCurrent: values?.documentCurrent,
										currentWorkingStatusDepartment: values?.currentWorkingStatusDepartment,
										currentWorkingStatusOffice: values?.currentWorkingStatusOffice,
									}),
									firstWorkingPlace: JSON.stringify({
										firstWorkingstartDate: values?.firstWorkingstartDate,
										firstWorkingPassedDate: values?.firstWorkingPassedDate,
										firstWorkingPosition: values?.firstWorkingPosition,
										firstWorkingInstitution: values?.firstWorkingInstitution,
										firstWorkingJobTitle: values?.firstWorkingJobTitle,
										firstWorkingOrganization: values?.firstWorkingOrganization,
										firstWorkingDepartment: values?.firstWorkingDepartment,
										firstWorkingOffice: values?.firstWorkingOffice,
										documentFirst: values?.documentFirst,
									}),
								},
							});
						}}
					>
						{(props) => (
							<Form>
								<ModalContent borderRadius={c.borderRadius}>
									<ModalHeader>{t('Edit')}</ModalHeader>
									<ModalCloseButton />
									<ModalBody>
										<Box>
											<Text fontSize="lg" fontWeight="bold" mt="2" mb="4">
												ក. ចូលបម្រើការងាររដ្ឋដំបូង
											</Text>
											<SimpleGrid columns={[1, 2, 3]} spacing={2}>
												<DateInputFormField
													{...props}
													label="កាលបរិច្ឆេទចូលបំរើការងាររដ្ឋដំបូង"
													name="firstWorkingstartDate"
													initialDate={initData?.firstWorkingstartDate}
												/>
												<DateInputFormField
													{...props}
													label="កាលបរិច្ឆេទតាំងស៊ប់"
													name="firstWorkingPassedDate"
													initialDate={initData?.firstWorkingPassedDate}
												/>
												<TextFormField {...props} label="មុខតំណែង" name="firstWorkingPosition" />
												<TextFormField {...props} label="ក្រសួង/ស្ថាប័ន" name="firstWorkingInstitution" />
												{/* <DropDownField
													{...props}
													label="ក្របខ័ណ្ឌ ឋានន្តរស័ក និងថ្នាក់"
													name="firstWorkingJobTitle"
													placeholder="សូមជ្រើសរើស"
													disabled={checkEmployeeEditPermission(currentUser?.role)}
													options={[
														{ label: 'អនុមន្រ្តីកម្រិត១', value: 'Level_7' },
														{ label: 'អនុមន្រ្តីកម្រិត២', value: 'Level_6' },
														{ label: 'អនុមន្រ្តីកម្រិត៣', value: 'Level_5' },
														{ label: 'នាយក្រមការ', value: 'Level_4' },
														{ label: 'ក្រមការដើមខ្សែ', value: 'Level_3' },
														{ label: 'ក្រមការ', value: 'Level_2' },
														{ label: 'កម្មសិក្សា', value: 'Level_1' },
													]}
												/> */}

												<TextFormField {...props} label="ក្របខ័ណ្ឌ ឋានន្តរស័ក និងថ្នាក់" name="firstWorkingJobTitle" />

												<TextFormField {...props} label="អង្គភាព" name="firstWorkingOrganization" />

												<TextFormField {...props} label="នាយកដ្ឋាន/អង្គភាព/មន្ទីរ" name="firstWorkingDepartment" />

												<TextFormField {...props} label="ការិយាល័យ" name="firstWorkingOffice" />

												<PreUploadFileFormField {...props} label={'ឯកសារយោង'} name={'documentFirst'} />
											</SimpleGrid>
											<Text fontSize="lg" fontWeight="bold" mt="4" mb="4">
												ខ. ស្ថានភាពមុខងារបច្ចុប្បន្ន
											</Text>

											<SimpleGrid columns={[1, 2, 3]} spacing={2}>
												{/* <TextFormFieldReadOnly
													{...props}
													label="មុខតំណែង"
													name="currentWorkingStatusPosition"
													placeholder={
														initData?.currentWorkingStatusPosition ? getPositionLabel(initData?.currentWorkingStatusPosition) : ''
													}
													disabled={true}
												/> */}

												<DropDownField
													{...props}
													label="មុខតំណែង"
													name="currentWorkingStatusPosition"
													placeholder="សូមជ្រើសរើស"
													disabled={true}
													options={[
														{ value: 1, label: 'អគ្គនាយក' },
														{ value: 2, label: 'អគ្គនាយករង' },
														{ value: 3, label: 'ប្រធាននាយកដ្ឋាន' },
														{ value: 4, label: 'អនុប្រធាននាយកដ្ឋាន' },
														{ value: 5, label: 'ប្រធានការិយាល័យ' },
														{ value: 6, label: 'អនុប្រធានការិយាល័យ' },
														{ value: 7, label: 'មន្រ្តីលក្ខន្តិកៈ' },
														{ value: 8, label: 'មន្រ្តីកម្មសិក្សា' },
													]}
												/>

												<DateInputFormField
													{...props}
													label="កាលបរិច្ឆេទទទួលមុខតំណែងចុងក្រោយ"
													name="currentWorkingStatusDateOfChangePosition"
													initialDate={initData?.currentWorkingStatusDateOfChangePosition}
													disabled
												/>

												<TextFormFieldReadOnly
													{...props}
													label={'អង្គភាព'}
													name="currentWorkingStatusOrganization"
													placeholder="និយ័តករធានារ៉ាប់រងកម្ពុជា"
													disabled={true}
												/>

												<DropDownField
													{...props}
													label="ក្របខ័ណ្ឌ ឋានន្តរស័ក និងថ្នាក់"
													name="currentWorkingStatusJobTitle"
													placeholder="សូមជ្រើសរើស"
													disabled={true}
													options={[
														{ label: 'អនុមន្រ្តីកម្រិត១', value: 'Level_7' },
														{ label: 'អនុមន្រ្តីកម្រិត២', value: 'Level_6' },
														{ label: 'អនុមន្រ្តីកម្រិត៣', value: 'Level_5' },
														{ label: 'នាយក្រមការ', value: 'Level_4' },
														{ label: 'ក្រមការដើមខ្សែ', value: 'Level_3' },
														{ label: 'ក្រមការ', value: 'Level_2' },
														{ label: 'កម្មសិក្សា', value: 'Level_1' },
													]}
												/>

												{/* <TextFormField {...props} label="ក្របខ័ណ្ឌ ឋានន្តរស័ក និងថ្នាក់" name="currentWorkingStatusJobTitle" /> */}

												<DateInputFormField
													{...props}
													label="កាលបរិច្ឆេទប្តូរក្របខ័ណ្ឌ ឋានន្តរស័ក និងថ្នាក់ចុងក្រោយ"
													name="currentWorkingStatusDateOfChangeJobTitle"
													initialDate={initData?.currentWorkingStatusDateOfChangeJobTitle}
													disabled={true}
												/>

												<PreUploadFileFormField {...props} label={'ឯកសារយោង'} name={'documentCurrent'} disabled={true} />

												<DropDownField
													{...props}
													label="នាយកដ្ឋាន"
													name="currentWorkingStatusDepartment"
													placeholder="សូមជ្រើសរើស"
													disabled={true}
													options={[
														{ label: 'នាយកដ្ឋានកិច្ចការទូទៅ', value: 'D_G_A' },
														{ label: 'នាយកដ្ឋានគ្រប់គ្រងអាជ្ញាបណ្ណ និងកិច្ចការគតិយុត្ត', value: 'D_L_L_A' },
														{ label: 'នាយកដ្ឋានត្រួតពិនិត្យ', value: 'D_S' },
														{ label: 'នាយកដ្ឋានអភិវឌ្ឍន៍ទីផ្សារ', value: 'D_M_D' },
														{ label: 'នាយកដ្ឋានស្រាវជ្រាវ បណ្តុះបណ្តាល និងសហប្រតិបត្តិការ', value: 'D_R_T_C' },
													]}
												/>

												<DropDownField
													{...props}
													label="ការិយាល័យ"
													name="currentWorkingStatusOffice"
													placeholder="សូមជ្រើសរើស"
													disabled={true}
													options={[
														{ label: 'ការិយាល័យរដ្ឋបាល', value: 'office1' },
														{ label: 'ការិយាល័យគណនេយ្យ', value: 'office2' },
														{ label: 'ការិយាល័យធនធានមនុស្ស', value: 'office3' },
														{ label: 'ការិយាល័យទំនាក់ទនងសាធារណៈ', value: 'office4' },
														{ label: 'ការិយាល័យគ្រប់គ្រងអាជ្ញាបណ្ណ', value: 'office5' },
														{ label: 'ការិយាល័យចុះបញ្ជីអន្តរការី', value: 'office6' },
														{ label: 'ការិយាល័យនីតិកម្ម និងផ្សះផ្សាវិវាទ', value: 'office7' },
														{ label: 'ការិយាល័យត្រួតពិនិត្យក្រុមហ៊ុនធានារ៉ាប់រងអាយុជីវិត', value: 'office8' },
														{ label: 'ការិយាល័យត្រួតពិនិត្យក្រុមហ៊ុនធានារ៉ាប់រងទូទៅ', value: 'office9' },
														{
															label: 'ការិយាល័យត្រួតពិនិត្យក្រុមហ៊ុនធានារ៉ាប់រងខ្នាតតូច​ និង​អន្តរការីធានារ៉ាប់រង',
															value: 'office10',
														},
														{ label: 'ការិយាល័យបទប្បញ្ញត្តិប្រុងប្រយ័ត្ន', value: 'office11' },
														{ label: 'ការិយាល័យផលិតផលធានារ៉ាប់រងអាយុជីវិត', value: 'office12' },
														{ label: 'ការិយាល័យផលិតផលធានារ៉ាប់រងទូទៅ', value: 'office13' },
														{ label: 'ការិយាល័យផលិតផលធានារ៉ាប់រងខ្នាតតូច', value: 'office14' },
														{ label: 'ការិយាល័យត្រួតពិនិត្យប្រតិបត្តិការទីផ្សារ', value: 'office15' },
														{ label: 'ការិយាល័យស្រាវជ្រាវ', value: 'office16' },
														{ label: 'ការិយាល័យបណ្តុះបណ្តាល', value: 'office17' },
														{ label: 'ការិយាល័យសហប្រតិបត្តិការ', value: 'office18' },
														{ label: 'ការិយាល័យបច្ចេកវិទ្យាធានារ៉ាប់រង', value: 'office19' },
													]}
												/>
											</SimpleGrid>
										</Box>
									</ModalBody>
									<ModalFooter bg="rgba(236, 240, 241,0.5)">
										<Button
											borderRadius={c.borderRadius}
											colorScheme="green"
											type="submit"
											isLoading={loading}
											loadingText={t('Save')}
											spinnerPlacement="start"
										>
											{t('Save')}
										</Button>
									</ModalFooter>
								</ModalContent>
							</Form>
						)}
					</Formik>
				</Modal>
			</Box>
		</>
	);
}

export default FirstWorkingPlaceAndCurrentWorkingStatus;
